import { useEffect, useRef, useState } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
let scriptPromise;

function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_URL;
      script.async = true;
      const timer = setTimeout(() => fail(), 15000);
      const fail = () => {
        clearTimeout(timer);
        script.remove();
        scriptPromise = undefined;
        reject(new Error('Turnstile unavailable'));
      };
      script.onload = () => {
        if (!window.turnstile) {
          fail();
          return;
        }
        clearTimeout(timer);
        resolve();
      };
      script.onerror = fail;
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

// eslint-disable-next-line react/prop-types
export default function Turnstile({ onVerify, onError, onExpire }) {
  const containerRef = useRef(null);
  const callbacks = useRef({ onVerify, onError, onExpire });
  callbacks.current = { onVerify, onError, onExpire };
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let widgetId = null;
    const fail = () => {
      if (cancelled) return;
      setFailed(true);
      callbacks.current.onError?.();
    };
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        widgetId = window.turnstile.render(containerRef.current, {
          sitekey: import.meta.env.VITE_TURNSTILE_SITE_KEY,
          callback: (token) => {
            if (!cancelled) {
              setFailed(false);
              callbacks.current.onVerify?.(token);
            }
          },
          'error-callback': fail,
          'expired-callback': () => {
            if (!cancelled) callbacks.current.onExpire?.();
          },
          theme: 'light',
          size: 'flexible',
        });
      })
      .catch(fail);
    return () => {
      cancelled = true;
      if (widgetId !== null && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [attempt]);

  return (
    <div>
      <div ref={containerRef} className="max-w-[280px] overflow-hidden sm:max-w-none" />
      {failed && (
        <div role="alert" className="mt-2 text-sm text-ink-muted">
          <p>Güvenlik doğrulaması yüklenemedi.</p>
          <button
            type="button"
            className="mt-2 font-semibold text-brand-700 underline"
            onClick={() => {
              setFailed(false);
              callbacks.current.onExpire?.();
              setAttempt((value) => value + 1);
            }}
          >
            Doğrulamayı yeniden dene
          </button>
        </div>
      )}
    </div>
  );
}
